import _ from 'lodash';
import angular from 'angular';
import template from './template.html';
import { EMPLOYER_REP_ROLES, roleOptions } from '../../constants/roles';
import { Async, Inject } from '../../../../base/decorators';
import BaseComponent from '../../../../base/component';
import { EmployerRepAuthTypes } from '../../../../../constants/employerRepAuthTypes';
import UtilsService from '../../../../../services/utilsService';

export class EmployerRepSignUpDetails extends BaseComponent {
    formData = {
        name: '',
        email: '',
        hrRole: EMPLOYER_REP_ROLES.HR,
        canEditEmail: true,
        errorMessage: null,
    };

    @Inject('$stateParams') $stateParams;

    @Inject('$state') $state;

    @Inject('$filter') $filter;

    @Inject('EmployerRepService') employerRepService;

    @Inject('$localStorage') $localStorage;

    @Inject('EnvSettings') EnvSettings;

    @Inject('NotificationService') notificationService;

    @Inject('ModalDialogService') modalDialogService;

    @Inject('RouteService') routeService;

    @Inject('AuthService') authService;

    @Inject('$cookies') $cookies;

    async setupComponentData() {
        UtilsService.cookiesOverride(this.$cookies);
        const options = { domain: window.location.hostname.split('.').slice(-2).join('.') };
        const { employerRepId, isInvitation, isCreateNewEmplRepAction, invitationId } = this.$stateParams;
        const positionId = _.get(this, '$localStorage.employerRepReturnUrlParams.positionId', null);

        const successUrl = this.$cookies.get('activationSuccessUrl');
        if (successUrl) {
            this.$cookies.remove('activationSuccessUrl', options);
        }

        if (!employerRepId && !positionId && !invitationId) {
            return this.goToLoginPage();
        }

        const { result: employerRep } = (invitationId
            ? await this.employerRepService.getEmployerRepByInvitationId(invitationId)
            : await this.employerRepService.getEmployerRepDetails(employerRepId)
        ) || {};

        await this.routeService.logout();
        this.authService.clearSession();

        if (employerRep) {
            this.auth0Account = employerRep.auth0Account;
            if (_.includes(['pending', 'active'], employerRep?.status?.current) && employerRep?.authType === EmployerRepAuthTypes.PAP) {
                // already registered, redirect to login
                this.alreadyRegistered = true;
                if (!this.auth0Account) {
                    this.goToLoginPage();
                    return;
                }
                this.authService.goToSsoLoginPage(this.EnvSettings.WebAppUrl, employerRep?.employerId, employerRep.email, employerRep?.isOnlyFastRatingAllowed, successUrl);
                return;
            }
            if (employerRep?.status?.current === 'inactive') {
                this.isInactive = true;
                return;
            }
            const name = this._getEmployerRepDisplayName({ employerRep, isCreateNewEmplRepAction, isInvitation });
            this.formData = this.getFromData(employerRep, !!isInvitation, isCreateNewEmplRepAction);
            this.translateData = { name };
            this.employerRepId = employerRep._id || employerRepId;
            this.userRoles = roleOptions;
            this.invitationId = invitationId;
        }
        this.isInvitation = !!isInvitation;
        this.isCreateNewEmplRepAction = isCreateNewEmplRepAction;
        this.tftMainLogoUrl = await this.routeService.getThemeSettingsAsync('tftMainLogoUrl') || '';
        this.successUrl = successUrl;
    }

    onNameChange(name) {
        this.formData.name = name;
    }

    onEmailChange(email) {
        this.formData.email = email;
    }

    onHrRoleChange(hrRole) {
        this.formData.hrRole = hrRole;
    }

    _getEmployerRepDisplayName({ employerRep, isCreateNewEmplRepAction, isInvitation }) {
        const isNameHidden = !isInvitation && isCreateNewEmplRepAction;
        return isNameHidden ? '' : (employerRep.name || '');
    }

    _getEmployerRepDisplayEmail({ employerRep, isCreateNewEmplRepAction, isInvitation }) {
        const isEmailHidden = !isInvitation && isCreateNewEmplRepAction;
        return isEmailHidden ? '' : (employerRep.email || '');
    }

    getFormHeaderTranslationId() {
        return this.auth0Account ? 'EMPLOYER_SIGN_UP_NEW' : 'EMPLOYER_SIGN_UP';
    }

    getFormDescriptionTranslationId() {
        if (this.auth0Account) {
            return 'REGISTRATION_CONFIRM_DETAILS_TEXT_NEW';
        }
        const isShowWInviteDescription = this.isInvitation || !this.isCreateNewEmplRepAction;
        return isShowWInviteDescription ? 'REGISTRATION_CONFIRM_DETAILS_TEXT' : 'REGISTRATION_CONFIRM_ALL_DETAILS_TEXT';
    }

    getFromData(employerRep, isInvitation, isCreateNewEmplRepAction) {
        const isNonHrRole = (employerRep.roles || []).includes(EMPLOYER_REP_ROLES.NON_HR);
        const hrRole = isNonHrRole ? EMPLOYER_REP_ROLES.NON_HR : EMPLOYER_REP_ROLES.HR;
        const name = this._getEmployerRepDisplayName({ employerRep, isCreateNewEmplRepAction, isInvitation });
        const email = this._getEmployerRepDisplayEmail({ employerRep, isCreateNewEmplRepAction, isInvitation });
        return {
            name,
            email,
            canEditEmail: !isInvitation,
            hrRole,
        };
    }

    @Async()
    async saveEmployerRepDetails() {
        if (!_.get(this, 'formData.name.length')) {
            this.formData.errorMessage = 'PLEASE_FILL_REQUIRED_FIELDS';

            return;
        }

        let { employerRepId, isInvitation, invitationId } = this.$stateParams;
        const positionId = _.get(this, '$localStorage.employerRepReturnUrlParams.positionId', null);
        const employerId = _.get(this, '$localStorage.employerRepReturnUrlParams.employerId', null);
        const actionType = _.get(this, '$localStorage.employerRepReturnUrlParams.actionType', null);
        const targetEmployerRepId = _.get(this, '$localStorage.employerRepReturnUrlParams.targetEmployerRepId', null);
        employerRepId = employerRepId || targetEmployerRepId || this.employerRepId;

        const { result: signUpResult } = await this.employerRepService.systemSignUpEmployerRep({
            employerRepId,
            positionId,
            employerId,
            actionType,
            name: this.formData.name,
            email: this.formData.email,
            role: this.formData.hrRole,
            isInvitation: !!isInvitation,
            host: `${this.EnvSettings.Auth0RedirectUrl}`,
            invitationId,
        });

        if (signUpResult.err) {
            this.notificationService.error('EMPLOYER_REP_REGISTRATION_FAILED_INVITATION_NOT_VALID');
            this.goToLoginPage();
            return;
        }

        _.set(this, '$localStorage.employerRepReturnUrlParams.targetEmployerRepId', signUpResult.employerRepId);

        const isExistsAndActiveAndBelongsToEmployer = signUpResult.isActiveEmployerRep && signUpResult.isBelongsToEmployer;
        const isExistsAndActiveAndNotBelongsToEmployer = signUpResult.isActiveEmployerRep && !signUpResult.isBelongsToEmployer;
        const isExistsAndInvitedBelongsToEmployer = signUpResult.isInvitedEmployerRep && signUpResult.isBelongsToEmployer;
        const isExistsAndInvitedNotBelongsToEmployer = signUpResult.isInvitedEmployerRep && !signUpResult.isBelongsToEmployer;
        const isInactiveUser = signUpResult.isInactiveEmployerRep || signUpResult.isInactiveRecruiter;
        const { isSso } = signUpResult;

        if (isInvitation && signUpResult.isNew) {
            this.goToSignUpPasswordPage(employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (signUpResult.isNew) {
            this.goToSignUpEmailPage(signUpResult.employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (isExistsAndActiveAndBelongsToEmployer) {
            this.notificationService.success('EMPLOYER_REP_REGISTRATION_EXIST');
            this.goToLoginPage();
        } else if (isExistsAndActiveAndNotBelongsToEmployer) {
            this.goToSignUpPasswordPage(employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (isExistsAndInvitedNotBelongsToEmployer) {
            this.goToSignUpPasswordPage(employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (isExistsAndInvitedBelongsToEmployer && signUpResult.isSameTargetEmails) {
            this.goToSignUpPasswordPage(signUpResult.employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (isExistsAndInvitedBelongsToEmployer && !signUpResult.isSameTargetEmails) {
            this.goToSignUpEmailPage(signUpResult.employerRepId, signUpResult.employerId, isSso, invitationId);
        } else if (isInactiveUser) {
            this.notificationService.error('EMPLOYER_REP_REGISTRATION_EXIST_INACTIVE_SIGNUP');
            this.goToLoginPage();
        } else {
            this.goToLoginPage();
        }
    }

    goToSignUpEmailPage(employerRepId, employerId, isSso) {
        this.$state.go('employerRep.signUp.email', { employerRepId, employerId, isSso });
    }

    goToSignUpPasswordPage(employerRepId, employerId, isSso, invitationId) {
        this.$state.go(`employerRep.signUp.password${invitationId ? 'New' : ''}`, { employerRepId, employerId, isSso, successUrl: this.successUrl, invitationId });
    }

    @Async()
    async goToLoginPage(employerRepId) {
        this.authService.logout(true);
        this.$state.go('login', { isEmployerRep: 1, employerRepId });
    }

    onUserRoleChange(value) {
        this.formData.hrRole = value;
    }
}

export const EmployerRepSignUpDetailsConfiguration = {
    template,
    controller: EmployerRepSignUpDetails,
    controllerAs: 'employerRepSignUpDetailsCtrl',
};

angular
    .module('TALENYA-FE')
    .component('employerRepSignUpDetails', EmployerRepSignUpDetailsConfiguration);
